<template>
  <v-card flat>
    <v-expansion-panels>
      <v-expansion-panel>
        <v-expansion-panel-header>Defaults</v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-textarea label="Message to customer" v-model="preferences.defaultMessage" outlined dense :rows="2" :rules="[fieldRules.maxLength(1000)]" hide-details="auto"/>
          <v-tooltip bottom max-width="300" nudge-top="20" nudge-left="100" open-delay="1000">
            <template v-slot:activator="{on}">
              <v-switch v-model="preferences.useNumber" hide-details>
                <span slot="label" v-on="on">Allow numbers to be entered</span>
              </v-switch>
            </template>
            If you are connected to QuickBooks and "Custom transaction numbers" is off (recommended),
            then this option should be turned off to let QuickBooks automatically generate an invoice number for you.
            If you leave this option on, then it will be possible to have duplicate invoice numbers in QuickBooks.
            If "Custom transaction numbers" is on, then you can specify your own invoice numbers, which will
            be used by QuickBooks. In this case, QuickBooks will not allow duplicate invoice numbers.
          </v-tooltip>
          <v-switch v-model="preferences.duplicateInvoiceNumbers" label="Allow duplicate numbers" hide-details/>
          <v-switch v-model="preferences.alwaysInBond" label="Pull product from in-bond" hide-details/>
          <v-switch v-model="preferences.defaultTaxInclusive" label="Tax inclusive amounts" hide-details/>
          <v-switch v-model="preferences.defaultCreateInvoice" label="Create invoice on Shipment" hide-details/>
          <v-switch v-model="preferences.allowDiscounts" label="Allow discounts"/>
          <v-text-field label="Default Cc" v-model="preferences.defaultCc" :rules="[fieldRules.maxLength(100)]" outlined dense :counter="100"
                        hint="Separate emails with a comma" hide-details/>
          <v-switch v-model="preferences.defaultCcSalesRep" label="Set Cc to sales rep"/>
          <v-text-field label="Default Bcc" v-model="preferences.defaultBcc" :rules="[fieldRules.maxLength(100)]" outlined dense :counter="100"
                        hint="Separate emails with a comma" hide-details/>
          <v-switch v-model="preferences.defaultBccSalesRep" label="Set Bcc to sales rep" hide-details/>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header>Sales orders</v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-switch v-model="preferences.usePicking" label="Use order pick status" hide-details
                  @change="!preferences.usePicking ? (preferences.pickBeforeShip = false,preferences.lineItemPicking = false) : ''"/>
          <v-switch v-model="preferences.lineItemPicking" label="Use line item picking" hide-details :disabled="!preferences.usePicking"/>
          <v-switch v-model="preferences.pickBeforeShip" label="Enforce picking before shipping" hide-details :disabled="!preferences.usePicking"/>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel v-if="company.connectedToAccounting">
        <v-expansion-panel-header>QuickBooks</v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-switch v-model="preferences.defaultQBO" label="Create in QuickBooks by default" hide-details/>
          <v-switch v-model="preferences.defaultAllowOnlinePayments" label="Allow online payments by default"/>
          <div class="mb-2">
            Custom QBO fields
            <v-tooltip bottom max-width="300" nudge-top="20" nudge-left="100">
              <template v-slot:activator="{on}">
                <v-icon v-on="on" class="ml-4 mt-n1">mdi-help-circle</v-icon>
              </template>
              If you have defined custom fields on sales forms in QuickBooks Online,
              the names of those fields can be specified to display information from Purtrak.
            </v-tooltip>
          </div>
          <v-text-field outlined dense hide-details class="mb-2" label="QBO Customer license # field" v-model="preferences.customerLicenseeFieldName"/>
          <v-text-field outlined dense hide-details class="mb-2" label="QBO Customer PO field" v-model="preferences.customerPOFieldName"/>
          <v-text-field outlined dense hide-details label="QBO Sales rep field" v-model="preferences.salesRepFieldName"/>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header>Customize print forms</v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-btn small class="ma-1" @click="customizeSalesOrder">Sales order</v-btn>
          <v-btn small class="ma-1" @click="customizeInvoice">Invoice</v-btn>
          <v-btn small class="ma-1" @click="customizePackingList">Packing list</v-btn>
          <v-btn small class="ma-1" @click="customizeDeliveryNote">Delivery note</v-btn>
          <v-btn small class="ma-1" @click="customizeEstimate">Estimate</v-btn>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
    <component :is="dialog" @done="dialog = null"/>
  </v-card>
</template>

<script>
export default {
  name: 'SalesPreferences',
  data() {
    return {
      preferences: {},
      dialog: null
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    }
  },
  watch: {
    preferences: {
      async handler() {
        const results = await this.$apiCall(`mutation SalesPreferences($input:SalesPreferencesInput!) {
                                              salesSetPreferences(input:$input) {
                                                usePicking,pickBeforeShip,lineItemPicking,defaultMessage
                                                defaultCreateInvoice,allowDiscounts,alwaysInBond
                                                defaultQBO,defaultAllowOnlinePayments,useNumber,duplicateInvoiceNumbers,
                                                customerLicenseeFieldName,customerPOFieldName,salesRepFieldName
                                                defaultTaxInclusive
                                                defaultCc,defaultBcc,defaultCcSalesRep,defaultBccSalesRep
                                              }
                                            }`,
                                            {input:this.preferences});
        if (!results.error)
          this.$emit('update', results.data.salesSetPreferences)
      },
      immediate: false,
      deep: true
    }
  },
  async created() {
    const results = await this.$apiCall(`query SalesPreferences {
                                          salesPreferences {
                                            usePicking,pickBeforeShip,lineItemPicking,defaultMessage
                                            defaultCreateInvoice,allowDiscounts,alwaysInBond
                                            defaultQBO,defaultAllowOnlinePayments,useNumber,duplicateInvoiceNumbers,
                                            customerLicenseeFieldName,customerPOFieldName,salesRepFieldName
                                            defaultTaxInclusive
                                            defaultCc,defaultBcc,defaultCcSalesRep,defaultBccSalesRep
                                          }
                                        }`,
                                        {});
    if (!results.error)
      this.preferences = results.data.salesPreferences;
  },
  methods: {
    customizeInvoice() {
      this.dialog = () => import(`@/views/sales/invoices/SalesInvoicesFormSetup`);
    },
    customizeSalesOrder() {
      this.dialog = () => import(`@/views/sales/salesOrders/SalesSalesOrdersFormSetup`);
    },
    customizePackingList() {
      this.dialog = () => import(`@/views/sales/shipments/SalesShipmentsPackingListFormSetup`);
    },
    customizeDeliveryNote() {
      this.dialog = () => import(`@/views/sales/shipments/SalesShipmentsDeliveryNoteFormSetup`);
    },
    customizeEstimate() {
      this.dialog = () => import(`@/views/sales/estimates/SalesEstimatesFormSetup`);
    }
  }
}
</script>